import {
  IInitialViewpoint,
  ITopicPoint,
  IViewpointBimFile,
  IViewpointCamera,
  IViewpointComponent,
} from 'Data/interfaces/Activities/IViewpointData';
import { BimBcfCamera, BimViewerHandle } from 'Data/interfaces/BimViewer/IBimViewerPort';
import { IViewpointEngine } from './IViewpointEngine';

/** Um modelo na cena, do jeito que o viewpoint precisa registrar. */
export interface IIfcLiteViewpointModel {
  /** Identificador do modelo na Coordly. Vira `BimFiles[].Reference`. */
  urn: string;
  name: string;
  /** Índice do modelo no motor — o mesmo que a seleção reporta. */
  modelIndex: number;
  isVisible: boolean;
}

/**
 * O estado que mora no React e o motor não conhece: quais modelos estão na cena,
 * o que está selecionado e o que está oculto. Vem por função porque é lido no
 * momento da captura, não no da montagem.
 */
export interface IIfcLiteViewpointContext {
  handle: BimViewerHandle | null;
  models: IIfcLiteViewpointModel[];
  /** Ids LOCAIS ao modelo, com o modelo de cada um em `modelIndices`. */
  selection: { expressIds: number[]; modelIndices?: number[] };
  /**
   * Ids locais ocultos. Sem modelo associado de propósito: `hide` no motor
   * esconde o id em TODOS os modelos que o contêm, então o oculto é resolvido
   * contra todos na hora de gravar.
   */
  hiddenIds: number[];
}

const ORIGINATING_SYSTEM = 'Coordly 3D (ifc-lite)';

/** Formato da ponte (camelCase) → DTO do BCF que o backend guarda (PascalCase). */
const toDtoCamera = (camera: BimBcfCamera, aspectRatio: number): IViewpointCamera => {
  const isOrthographic = typeof camera.viewToWorldScale === 'number';

  const dto: IViewpointCamera = {
    Type: isOrthographic ? 'Orthogonal' : 'Perspective',
    ViewPoint: {
      X: camera.cameraViewPoint.x,
      Y: camera.cameraViewPoint.y,
      Z: camera.cameraViewPoint.z,
    },
    Direction: {
      X: camera.cameraDirection.x,
      Y: camera.cameraDirection.y,
      Z: camera.cameraDirection.z,
    },
    UpVector: {
      X: camera.cameraUpVector.x,
      Y: camera.cameraUpVector.y,
      Z: camera.cameraUpVector.z,
    },
    AspectRatio: aspectRatio,
  };

  if (isOrthographic) {
    dto.ViewToWorldScale = camera.viewToWorldScale;
  } else {
    dto.FieldOfView = camera.fieldOfView ?? 45;
  }

  return dto;
};

/** O caminho de volta. */
const toBridgeCamera = (dto: IViewpointCamera): BimBcfCamera => {
  const camera: BimBcfCamera = {
    cameraViewPoint: { x: dto.ViewPoint.X, y: dto.ViewPoint.Y, z: dto.ViewPoint.Z },
    cameraDirection: { x: dto.Direction.X, y: dto.Direction.Y, z: dto.Direction.Z },
    cameraUpVector: { x: dto.UpVector.X, y: dto.UpVector.Y, z: dto.UpVector.Z },
  };

  // Ortográfica é decidida pelo `Type`, não pela presença do campo: BCF de fora
  // pode trazer os dois preenchidos.
  if (dto.Type === 'Orthogonal') {
    camera.viewToWorldScale = dto.ViewToWorldScale;
  } else {
    camera.fieldOfView = dto.FieldOfView ?? 45;
  }

  return camera;
};

/**
 * Viewpoint sobre o motor ifc-lite.
 *
 * Duas coisas o distinguem do lado Autodesk, e as duas são vantagem:
 *
 * - **O GlobalId é nativo.** O `IfcGuid` do BCF sai direto do data model, sem a
 *   conversão de GUID comprimido para UUID nem a busca por `LcOaNodeGuid` que o
 *   restore da Autodesk precisa fazer.
 * - **A visibilidade já está em memória.** Não há travessia de árvore com uma
 *   chamada de propriedades por folha; o app já sabe o que escondeu.
 *
 * O que NÃO entra ainda: `ClippingPlanes`. O corte do motor é por eixo em
 * porcentagem das dimensões do modelo, e converter para o ponto e normal que o
 * BCF pede exige a caixa do modelo, que o app não tem. Gravar errado seria pior
 * que não gravar.
 */
export class IfcLiteViewpointEngine implements IViewpointEngine {
  constructor(
    private readonly getContext: () => IIfcLiteViewpointContext,
    private readonly getAspectRatio: () => number,
  ) {}

  isReady(): boolean {
    const { handle, models } = this.getContext();
    return !!handle && models.length > 0 && !!handle.getCamera();
  }

  async capture(): Promise<IInitialViewpoint> {
    const { handle, models, selection, hiddenIds } = this.getContext();

    if (!handle) { throw new Error('Motor 3D não está montado'); }

    const camera = handle.getCamera();
    if (!camera) { throw new Error('Motor 3D não devolveu a câmera'); }

    const snapshot = await handle.captureSnapshot();
    const selectionComponents = this.componentsFor(handle, models, this.selectionByModel(selection));
    const hiddenComponents = this.componentsFor(
      handle,
      models,
      // Oculto vale para todo modelo que contenha o id, espelhando o `hide`.
      new Map(models.map((model) => [model.modelIndex, hiddenIds])),
    );

    return {
      Index: 0,
      SnapshotBase64: snapshot ?? undefined,
      Camera: toDtoCamera(camera, this.getAspectRatio()),
      Selection: selectionComponents.length > 0 ? selectionComponents : undefined,
      Visibility: {
        // Sempre "esconder poucos": o app sabe exatamente o que ocultou, então
        // não há por que listar o que está visível — que num modelo grande seria
        // um payload de megabytes.
        DefaultVisibility: true,
        Exceptions: hiddenComponents,
        SpacesVisible: false,
      },
      BimFiles: models.map((model): IViewpointBimFile => ({
        Filename: model.name,
        Reference: model.urn,
        IsVisible: model.isVisible,
      })),
    };
  }

  apply(viewpoint: IInitialViewpoint): void {
    const { handle, models } = this.getContext();
    if (!handle) { return; }

    if (viewpoint.Camera) {
      handle.setCamera(toBridgeCamera(viewpoint.Camera));
    }

    this.applyVisibility(handle, models, viewpoint);
    this.applySelection(handle, models, viewpoint);
  }

  /** Seleção agrupada por modelo, com ids locais. */
  private selectionByModel(
    selection: IIfcLiteViewpointContext['selection'],
  ): Map<number, number[]> {
    const byModel = new Map<number, number[]>();

    selection.expressIds.forEach((expressId, i) => {
      // Sem `modelIndices` (bundle antigo) tudo cai no primeiro modelo, que é o
      // comportamento do viewer de arquivo único.
      const modelIndex = selection.modelIndices?.[i] ?? 0;
      const ids = byModel.get(modelIndex);
      if (ids) { ids.push(expressId); } else { byModel.set(modelIndex, [expressId]); }
    });

    return byModel;
  }

  /** Traduz ids locais em componentes do BCF, identificados por GlobalId. */
  private componentsFor(
    handle: BimViewerHandle,
    models: IIfcLiteViewpointModel[],
    idsByModel: Map<number, number[]>,
  ): IViewpointComponent[] {
    const components: IViewpointComponent[] = [];

    for (const model of models) {
      const expressIds = idsByModel.get(model.modelIndex);
      if (!expressIds || expressIds.length === 0) { continue; }

      for (const { expressId, globalId } of handle.getGlobalIds(expressIds, model.modelIndex)) {
        components.push({
          IfcGuid: globalId,
          AuthoringToolId: globalId,
          OriginatingSystem: ORIGINATING_SYSTEM,
          ComponentDetails: {
            ModelUrn: model.urn,
            ModelName: model.name,
            DbId: String(expressId),
          },
        });
      }
    }

    return components;
  }

  private applyVisibility(
    handle: BimViewerHandle,
    models: IIfcLiteViewpointModel[],
    viewpoint: IInitialViewpoint,
  ): void {
    const visibility = viewpoint.Visibility;
    handle.showAll();

    if (!visibility) { return; }

    const exceptions = this.localIdsFor(handle, models, visibility.Exceptions ?? []);
    if (exceptions.length === 0) {
      // Sem exceções, `DefaultVisibility: false` esconderia tudo e deixaria a
      // tela preta. O `showAll` acima já é o estado certo.
      return;
    }

    if (visibility.DefaultVisibility) {
      handle.hide(exceptions);
    } else {
      handle.isolate(exceptions);
    }
  }

  private applySelection(
    handle: BimViewerHandle,
    models: IIfcLiteViewpointModel[],
    viewpoint: IInitialViewpoint,
  ): void {
    handle.clearSelection();

    const selection = viewpoint.Selection ?? [];
    if (selection.length === 0) { return; }

    let additive = false;
    for (const model of models) {
      const componentsOfModel = selection.filter(
        (component) => !component.ComponentDetails?.ModelUrn
          || component.ComponentDetails.ModelUrn === model.urn,
      );
      if (componentsOfModel.length === 0) { continue; }

      const globalIds = componentsOfModel
        .map((component) => component.IfcGuid)
        .filter((guid): guid is string => !!guid);

      for (const { expressId } of handle.getExpressIds(globalIds, model.modelIndex)) {
        handle.select(expressId, { additive, modelIndex: model.modelIndex });
        additive = true;
      }
    }
  }

  /** GlobalIds do viewpoint → ids locais, resolvidos em cada modelo da cena. */
  private localIdsFor(
    handle: BimViewerHandle,
    models: IIfcLiteViewpointModel[],
    components: IViewpointComponent[],
  ): number[] {
    const globalIds = components
      .map((component) => component.IfcGuid)
      .filter((guid): guid is string => !!guid);

    if (globalIds.length === 0) { return []; }

    const ids = new Set<number>();
    for (const model of models) {
      for (const { expressId } of handle.getExpressIds(globalIds, model.modelIndex)) {
        ids.add(expressId);
      }
    }

    return Array.from(ids);
  }
}
